import { useState, useCallback, useEffect } from 'react';
import { ActivityFeed } from './components/ActivityFeed';
import { ActivityDetailModal } from './components/ActivityDetailModal';
import { CreatePixelModal } from './components/CreatePixelModal';
import { Settings } from './components/Settings';
import { Sidebar, Filters, DateRange } from './components/Sidebar';
import { StatsBar } from './components/StatsBar';
import { ToastContainer, ToastData } from './components/Toast';
import { useEventStream } from './hooks/useEventStream';
import { ActivityEvent, getStats, Stats } from './utils/api';

type View = 'activity' | 'settings';

const defaultFilters: Filters = {
  dateRange: '7d',
  showOpened: true,
  showNotOpened: true,
};

function dateRangeToSince(range: DateRange): string | undefined {
  const now = Date.now();
  switch (range) {
    case 'today':
      return new Date(now - 24 * 60 * 60 * 1000).toISOString();
    case '7d':
      return new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
    case '30d':
      return new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
    case 'all':
      return undefined;
  }
}

function App() {
  const [view, setView] = useState<View>('activity');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    // Check notification permission
    if ('Notification' in window && Notification.permission === 'granted') {
      setAlertsEnabled(true);
    }
  }, []);

  // Fetch stats for sidebar
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const addToast = useCallback(
    (message: string, type: 'success' | 'error' | 'info') => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { id, message, type }]);
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleNewEvent = useCallback(
    (event: ActivityEvent, isLiveUpdate: boolean) => {
      // Only show toast/notification for live updates, not initial load
      if (isLiveUpdate) {
        // Show toast
        addToast(
          `Email opened: ${event.subject || event.to_email || 'Unknown'}`,
          'success'
        );

        // Show browser notification
        if (
          alertsEnabled &&
          'Notification' in window &&
          Notification.permission === 'granted'
        ) {
          new Notification('Email Opened', {
            body: `${event.to_email || 'Unknown recipient'} opened "${event.subject || 'No subject'}"`,
            icon: '/favicon.svg',
          });
        }
      }

      // Update activity feed (always, for both initial and live)
      const addEvent = (window as any).__addActivityEvent;
      if (addEvent) {
        addEvent(event);
      }
    },
    [alertsEnabled, addToast]
  );

  const { connected } = useEventStream({
    enabled: view === 'activity',
    onEvent: handleNewEvent,
    pollInterval: 5000,
  });

  return (
    <div className="app">
      <header className="header">
        <h1>MailTracker</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div className="connection-status">
            <span
              className={`connection-dot ${connected ? 'connected' : ''}`}
            ></span>
            {connected ? 'Connected' : 'Disconnected'}
          </div>
          <nav className="header-nav">
            <button
              className={view === 'activity' ? 'active' : ''}
              onClick={() => setView('activity')}
            >
              Activity
            </button>
            <button
              className={view === 'settings' ? 'active' : ''}
              onClick={() => setView('settings')}
            >
              Settings
            </button>
          </nav>
        </div>
      </header>

      <main className="main">
        <div className="app-layout">
          <div className="main-content">
            {view === 'activity' && (
              <>
                <StatsBar />
                <div style={{ marginBottom: '1.5rem' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    + New Tracking Pixel
                  </button>
                </div>
                <ActivityFeed
                  onNewEvent={handleNewEvent}
                  since={dateRangeToSince(filters.dateRange)}
                  onSelectEmail={setSelectedEmailId}
                />
              </>
            )}

            {view === 'settings' && (
              <Settings
                onSuccess={(msg) => addToast(msg, 'success')}
                alertsEnabled={alertsEnabled}
                setAlertsEnabled={setAlertsEnabled}
              />
            )}
          </div>
          <aside className="sidebar">
            {view === 'activity' && (
              <Sidebar
                filters={filters}
                onFiltersChange={setFilters}
                stats={stats ? {
                  totalOpens: stats.totalOpens,
                  uniqueEmails: stats.uniqueEmails,
                  todayOpens: stats.todayOpens,
                } : undefined}
              />
            )}
          </aside>
        </div>
      </main>

      {showCreateModal && (
        <CreatePixelModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(msg) => addToast(msg, 'success')}
          onError={(msg) => addToast(msg, 'error')}
        />
      )}

      {selectedEmailId && (
        <ActivityDetailModal
          emailId={selectedEmailId}
          onClose={() => setSelectedEmailId(null)}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default App;
